import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
    Building2, User, X, Send, Phone, Mail, MessageCircle, Briefcase, Check, Network
} from 'lucide-react';
import api from '../../lib/api';

export function DemoRequestModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        company_name: '', contact_name: '', email: '', phone: '', company_size: '',
        organization_type: '', n_agencies: '', current_crm: '', message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            await api.post('/contact/demo-request', formData);
            setSubmitted(true);
        } catch (err) {
            console.log('Demo request:', formData);
            setSubmitted(true);
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({ company_name: '', contact_name: '', email: '', phone: '', company_size: '', message: '' });
        setSubmitted(false);
        setError('');
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
                onClick={onClose}>
                <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-gradient-to-br from-slate-900 to-slate-950 sm:rounded-3xl w-full max-w-lg border-0 sm:border border-white/10 shadow-2xl min-h-screen sm:min-h-0 sm:my-4"
                    onClick={e => e.stopPropagation()}>
                    <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-md p-4 sm:p-6 pb-3 sm:pb-4 border-b border-white/10">
                        <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                            <X size={20} />
                        </button>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D]">
                                <MessageCircle className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-bold text-white">Demander une démo</h2>
                        </div>
                        <p className="text-white/60 text-sm">Un commercial vous contactera sous 24h pour une démonstration personnalisée.</p>
                    </div>
                    <div className="p-4 sm:p-6 pb-8">
                        {submitted ? (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8">
                                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                                    <Check className="w-8 h-8 text-green-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">Demande envoyée !</h3>
                                <p className="text-white/60 mb-6">Notre équipe commerciale vous contactera très prochainement.</p>
                                <Button onClick={() => { resetForm(); onClose(); }} className="bg-white/10 hover:bg-white/20 text-white">Fermer</Button>
                            </motion.div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{error}</div>}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-white/70 mb-1.5"><Building2 className="w-4 h-4 inline mr-1" />Nom de l&apos;agence *</label>
                                        <input type="text" name="company_name" value={formData.company_name} onChange={handleChange} required placeholder="Immo Excellence"
                                            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#681793] focus:ring-1 focus:ring-[#681793] transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-white/70 mb-1.5"><Briefcase className="w-4 h-4 inline mr-1" />Taille de l&apos;équipe</label>
                                        <select name="company_size" value={formData.company_size} onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#681793] focus:ring-1 focus:ring-[#681793] transition-colors">
                                            <option value="" className="bg-slate-900">Sélectionner</option>
                                            <option value="1-5" className="bg-slate-900">1-5 agents</option>
                                            <option value="6-15" className="bg-slate-900">6-15 agents</option>
                                            <option value="16-50" className="bg-slate-900">16-50 agents</option>
                                            <option value="50+" className="bg-slate-900">50+ agents</option>
                                        </select>
                                    </div>
                                    <div>
                                        {/* Question CRM actuel retirée : intégrations externes hors scope V1 */}
                                    </div>
                                </div>

                                {/* AXE MULTI-AGENCE — différencie agence indépendante / groupe / réseau */}
                                <div className="p-3 rounded-xl border border-[#A855F7]/20 bg-gradient-to-br from-[#1a0f2e]/40 to-transparent">
                                    <label className="block text-sm font-medium text-white/85 mb-2">
                                        <Network className="w-4 h-4 inline mr-1.5 text-[#C084FC]" />Votre structure
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { v: 'independent', label: 'Agence indépendante', sub: '1 point de vente' },
                                            { v: 'small_group', label: 'Petit groupe', sub: '2 à 5 agences' },
                                            { v: 'network', label: 'Réseau régional', sub: '6 à 20 agences' },
                                            { v: 'large_network', label: 'Grand réseau', sub: '20+ agences' },
                                        ].map((o) => (
                                            <button
                                                key={o.v}
                                                type="button"
                                                onClick={() => setFormData((p) => ({ ...p, organization_type: o.v }))}
                                                className={`text-left p-2.5 rounded-lg border transition-all ${
                                                    formData.organization_type === o.v
                                                        ? 'border-[#A855F7] bg-[#A855F7]/15 text-white'
                                                        : 'border-white/10 bg-white/[0.02] text-white/65 hover:border-white/20 hover:text-white/85'
                                                }`}
                                                data-testid={`org-${o.v}`}
                                            >
                                                <div className="text-xs font-semibold">{o.label}</div>
                                                <div className="text-[10px] text-white/40 mt-0.5">{o.sub}</div>
                                            </button>
                                        ))}
                                    </div>
                                    {(formData.organization_type === 'small_group' || formData.organization_type === 'network' || formData.organization_type === 'large_network') && (
                                        <div className="mt-3">
                                            <label className="block text-xs text-white/55 mb-1">Nombre exact d&apos;agences (si connu)</label>
                                            <input
                                                type="number"
                                                name="n_agencies"
                                                min="2"
                                                max="500"
                                                value={formData.n_agencies}
                                                onChange={handleChange}
                                                placeholder="ex. 14"
                                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-[#A855F7] focus:ring-1 focus:ring-[#A855F7] transition-colors"
                                                data-testid="input-n-agencies"
                                            />
                                            <p className="text-[10px] text-[#C084FC] mt-1.5">
                                                ✓ Providence (notre IA multi-agence) sera incluse dans votre démo.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {/* Indication compatibilité CRM retirée */}
                                <div>
                                    <label className="block text-sm font-medium text-white/70 mb-1.5"><User className="w-4 h-4 inline mr-1" />Votre nom *</label>
                                    <input type="text" name="contact_name" value={formData.contact_name} onChange={handleChange} required placeholder="Jean Dupont"
                                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#681793] focus:ring-1 focus:ring-[#681793] transition-colors" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-white/70 mb-1.5"><Mail className="w-4 h-4 inline mr-1" />Email *</label>
                                        <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="contact@agence.fr"
                                            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#681793] focus:ring-1 focus:ring-[#681793] transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-white/70 mb-1.5"><Phone className="w-4 h-4 inline mr-1" />Téléphone</label>
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="06 12 34 56 78"
                                            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#681793] focus:ring-1 focus:ring-[#681793] transition-colors" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-white/70 mb-1.5"><MessageCircle className="w-4 h-4 inline mr-1" />Message (optionnel)</label>
                                    <textarea name="message" value={formData.message} onChange={handleChange} rows={3} placeholder="Décrivez vos besoins..."
                                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#681793] focus:ring-1 focus:ring-[#681793] transition-colors resize-none" />
                                </div>
                                <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white font-bold py-3 h-auto">
                                    {submitting ? (
                                        <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Envoi en cours...</span>
                                    ) : (
                                        <span className="flex items-center gap-2"><Send size={18} />Envoyer ma demande</span>
                                    )}
                                </Button>
                                <p className="text-xs text-white/40 text-center">
                                    En soumettant ce formulaire, vous acceptez notre{' '}
                                    <Link to="/privacy" className="text-[#FF914D] hover:underline">politique de confidentialité</Link>.
                                </p>
                            </form>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
